import * as React from 'react';
// Import props.
import {
	ComparativeDataHeaderProps,
	ComparativeDataRowProps,
	ComparativePublicDwellingSummaryProps,
} from '@app/props';
// Import enumerations.
import {
	AppThemeColor,
} from '@app/enumerations';
// Import services.
import {
	AddressService,
	StringService,
} from '@app/services';
// Import interfaces 
import {
	IAddress,
} from '@app/interfaces';
// Import material ui modules
import {
	HouseOutlined,
} from '@mui/icons-material';

interface UseComparativePublicDwellingSummaryParameters {
	props: ComparativePublicDwellingSummaryProps;
}

interface UseComparativePublicDwellingSummaryResults {
	applyDwellingComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyDwellingAddressComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	applyDwellingLimitsComparativeDataRowProps: () => ComparativeDataRowProps[];
	applyDwellingLimitsComparativeDataHeaderProps: () => ComparativeDataHeaderProps;
	dwellingAddressBaseId: string;
	dwellingLimitsBaseId: string;
	showAddressSubheader: boolean
}

const useComparativePublicDwellingSummary = (params: UseComparativePublicDwellingSummaryParameters): UseComparativePublicDwellingSummaryResults => {

	// #region exported methods

	const applyDwellingComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${dwellingBaseId}-header`,
			isSticky: areHeadersSticky,
			title: 'Dwelling',
			titleIcon: HouseOutlined,
			titleFontSize: 'md',
			paddingProps: {
				bottom: 0,
				top: 1,
			},
		};
	}

	/**
	 * Returns dwelling address comparative data header props. 
	 **/
	const applyDwellingAddressComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${dwellingAddressBaseId}-header`,
			isSticky: areHeadersSticky,
			title: singleLineAddress,
			titleColor: AppThemeColor.INFO,
			titleFontSize: 'xxsm',
			titleFontStyle: 'italic',
			titleFontWeight: 'normal',
			paddingProps: {
				bottom: 1,
				top: 1,
			},
		};
	};

	/**
	 * Returns dwelling limits comparative data row props. 
	 **/
	const applyDwellingLimitsComparativeDataRowProps = (): ComparativeDataRowProps[] => {
		return [
			{
				fieldLabel: 'Dwelling Value',
				oldValue: buildingLimitCurrencyLabel,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
			{
				fieldLabel: 'Personal Property',
				oldValue: contentsLimitCurrencyLabel,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
			{
				fieldLabel: 'Appurtenant Structures',
				oldValue: structuresLimitCurrencyLabel,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
			{
				fieldLabel: 'Additional Living Expense',
				oldValue: aleLimitCurrencyLabel,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
			{
				fieldLabel: 'Total Primary Limit',
				oldValue: totalPrimaryLimitCurrencyLabel,
				hideNewValueIfSame: true,
				hideNewValueIfUndefinedOrEmpty: true,
			},
		];
	};

	/**
	 * Returns dwelling limits comparative data header props. 
	 **/
	const applyDwellingLimitsComparativeDataHeaderProps = (): ComparativeDataHeaderProps => {
		return {
			id: `${dwellingAddressBaseId}-dwelling-limits-header`,
			isSticky: false,
			title: 'Coverage Limits',
			titleFontSize: 'sm',
			paddingProps: {
				bottom: .5,
				top: .5,
			},
		};
	};

	// #endregion exported methods


	// #region private methods

	// #endregion private methods


	// #region variables

	const areHeadersSticky: boolean = React.useMemo(() => {
		return params.props.areHeadersSticky ?? false;
	}, [params.props.areHeadersSticky]);

	const dwellingBaseId: string = React.useMemo(() => {
		return`${ params.props.id}-dwelling`;
	}, [params.props.id]);

	const dwellingAddressBaseId: string = React.useMemo(() => {
		return `${dwellingBaseId}-address`;
	}, [dwellingBaseId]);

	const dwellingLimitsBaseId: string = React.useMemo(() => {
		return `${dwellingBaseId}-limits`;
	}, [dwellingBaseId]);

	const buildingLimit: number = React.useMemo(() => params.props.building?.limits?.building ?? 0, [params.props.building?.limits?.building]);
	const contentsLimit: number = React.useMemo(() => params.props.building?.limits?.contents ?? 0, [params.props.building?.limits?.contents]);
	const structuresLimit: number = React.useMemo(() => params.props.building?.limits?.otherStructures ?? 0, [params.props.building?.limits?.otherStructures]);
	const aleLimit: number = React.useMemo(() => params.props.building?.limits?.additionalLivingExpense ?? 0, [params.props.building?.limits?.additionalLivingExpense]);

	const buildingLimitCurrencyLabel: string = React.useMemo(() => StringService.getInstance().toCurrencyString(buildingLimit), [buildingLimit]);
	const contentsLimitCurrencyLabel: string = React.useMemo(() => StringService.getInstance().toCurrencyString(contentsLimit), [contentsLimit]);
	const structuresLimitCurrencyLabel: string = React.useMemo(() => StringService.getInstance().toCurrencyString(structuresLimit), [structuresLimit]);
	const aleLimitCurrencyLabel: string = React.useMemo(() => StringService.getInstance().toCurrencyString(aleLimit), [aleLimit]);

	const totalPrimaryLimit: number = React.useMemo(() => {
		return buildingLimit
			+ contentsLimit
			+ structuresLimit
			+ aleLimit;
	}, [
		buildingLimit,
		contentsLimit,
		structuresLimit,
		aleLimit,
	]);

	const totalPrimaryLimitCurrencyLabel: string = React.useMemo(() => StringService.getInstance().toCurrencyString(totalPrimaryLimit), [totalPrimaryLimit]);
	const addressRecord: IAddress = React.useMemo(() => {
		return {
			addressLine1: params.props.building?.address?.address?.addressLine1 ?? String.empty(),
			addressLine2: params.props.building?.address?.address?.addressLine2 ?? String.empty(),
			city: params.props.building?.address?.address?.city ?? String.empty(),
			state: params.props.building?.address?.address?.state ?? String.empty(),
			postalCode: params.props.building?.address?.address?.postalCode ?? String.empty(),
		};
	}, [params.props.building?.address?.address]);
	const singleLineAddress: string = React.useMemo(() => AddressService.getInstance().formatAddressToSingleLineString(addressRecord), [addressRecord]);

	const showAddressSubheader: boolean = React.useMemo(() => {
		return !String.isNullOrWhiteSpace(singleLineAddress);
	}, [singleLineAddress]);

	// #endregion private variables

	// #region lifecycle methods.

	// #endregion lifecycle methods.

	return {
		applyDwellingComparativeDataHeaderProps,
		applyDwellingAddressComparativeDataHeaderProps,
		applyDwellingLimitsComparativeDataRowProps,
		applyDwellingLimitsComparativeDataHeaderProps,
		dwellingAddressBaseId,
		dwellingLimitsBaseId,
		showAddressSubheader,
	};
}

// Export parameters.
export {
	UseComparativePublicDwellingSummaryParameters,
}

// Export results.
export {
	UseComparativePublicDwellingSummaryResults,
}

export default useComparativePublicDwellingSummary;